import type { HydraErrorType } from '@/lib/services/Hydra/_hydraError'
import React from 'react'
import T from '@/components/Helpers/Translate'
import { getHttpStatusMessage } from '@/lib/utils/errors/httpStatusMessages'
import useGlobalStore from '@/store/useGlobalStore'
import type { SupportedLanguage } from '@/lib/utils/localization/getSupportedLanguages'
import { Alert, AlertDescription, AlertTitle } from '../Alert'

type Props = {
  error: HydraErrorType | null
}

type ErrorMessages = {
  mainMessage: string
  detailsMessage: string | null
  statusCode: string | number | null
}

const getErrorMessages = (
  error: HydraErrorType,
  language: SupportedLanguage,
): ErrorMessages => {
  const statusCode = error.error?.code ?? null
  const apiMessage = error?.error?.message
  const apiErrorMessage = error?.error?.error_message

  const translatedMessage = statusCode
    ? getHttpStatusMessage(statusCode, language)
    : null

  if (translatedMessage) {
    const detailsMessage = apiMessage || apiErrorMessage || null
    return {
      mainMessage: translatedMessage,
      detailsMessage,
      statusCode,
    }
  }

  const fallbackMessage = apiMessage || apiErrorMessage
  if (fallbackMessage) {
    return {
      mainMessage: fallbackMessage,
      detailsMessage: null,
      statusCode,
    }
  }

  return {
    mainMessage: 'error_occurred',
    detailsMessage: null,
    statusCode,
  }
}

export const ErrorAlert = ({ error }: Props) => {
  const {
    app: { language },
  } = useGlobalStore()

  if (!error) return null

  const { mainMessage, detailsMessage, statusCode } = getErrorMessages(
    error,
    language,
  )

  return (
    <div data-test="error-alert">
      <Alert variant="destructive" className="my-3">
        <AlertTitle>
          <T>error</T>
        </AlertTitle>
        <AlertDescription className="text-sm break-words md:text-base">
          <div>
            {statusCode && (
              <span className="font-semibold">{statusCode}: </span>
            )}
            {mainMessage}
          </div>
          {detailsMessage && (
            <p className="mt-2 text-xs opacity-80">{detailsMessage}</p>
          )}
        </AlertDescription>
      </Alert>
    </div>
  )
}
